import { Injectable } from '@angular/core';
import { SessionService } from '@innovation-os-ng/core/auth';
import { Tenant } from '@innovation-os-ng/core/models';
import { AuthContextSelectors, skipper, TenantSelectors } from '@innovation-os-ng/core/state';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  constructor(private readonly sessionService: SessionService, private readonly store: Store) {}

  /**
   * Request the availability of a feature identified by its featureID
   *
   * @param checkFeatureId string Identifier of the feature
   *
   * @return A Promise<boolean> telling the availability of this feature
   */
  public async isFeatureEnabled(checkFeatureId: string): Promise<boolean> {
    return this.getFeatureFlags().then((features) => features && features.indexOf(checkFeatureId) > -1);
  }

  /**
   * Checks if all feature flags are enabled.
   *
   * @param {Array} featureFlagIds - An array containing the list of feature flag ids
   * @returns {boolean} - True if all feature flags are enabled, otherwise false.
   */
  public async areAllFeatureFlagsEnabled(featureFlagIds: Array<string>): Promise<boolean> {
    const featureFlagPromises = featureFlagIds.map((featureId: string) => this.isFeatureEnabled(featureId));

    return Promise.all(featureFlagPromises)
      .then((statusList: Array<boolean>) => statusList.every((status: boolean) => status))
      .catch(() => false);
  }

  /**
   * Fetch the list of feature flag of current tenant
   *
   * @returns List of feature flags
   */
  public async getFeatureFlags(): Promise<Array<string>> {
    // Sometimes, someone asks for features before authenticating
    if (!(await this.sessionService.isAuthenticated())) {
      return Promise.resolve([]);
    }

    return new Promise((resolve) => {
      this.store
        .select(TenantSelectors.selectCurrentTenant)
        .pipe(skipper())
        .subscribe((tenant: Tenant) => {
          console.assert(!!tenant, 'tenant should not be null')
          this.store.select(AuthContextSelectors.selectTenantFeatures(tenant.tenantUri)).pipe(skipper()).subscribe((tenantFeatures: Array<string>) => {
            resolve(tenantFeatures)
          });
        });
    });
  }
}
